import { useEffect, useMemo, useState } from 'react';
import { Placeholder } from 'react-bootstrap';
import { useShallow } from 'zustand/react/shallow';
import { I18n } from '@/i18n/I18n';
import { RequirementParams } from '@/backend/access/requirement/types';
import { RequirementCategoryType } from '@/backend/access/requirementEvidenceCategory/types';
import { RequirementEvidenceCategory } from '@/backend/models/requirementEvidenceCategory';
import { EvidenceSubmission } from '@/backend/models/evidenceSubmission';
import { VerificationCriteriaStatus } from '@/backend/access/verificationCriteria/types';
import { useRequirementDocumentationStore } from '@/frontend/store/RequirementDocumentationStore';
import { useRequirementsStore } from '@/frontend/store/RequirementsStore';
import { computeReviewProgress, reviewableSubmissions } from '@/frontend/utils/reviewProgress';
import { UserAssignmentSelect } from '../UserAssignmentSelect';
import { SubmissionInformation } from './SubmissionInformation';
import { EvidenceReviewSection } from './EvidenceReviewSection';
import { FileCard } from '../files/FileCard';

interface RequirementReviewTabProps {
  requirement: RequirementParams;
  projectId?: string;
}

export function RequirementReviewTab({ requirement, projectId }: RequirementReviewTabProps) {
  const fetchCategories = useRequirementDocumentationStore((s) => s.fetchCategories);
  const fetchEvidenceItemsByProjectId = useRequirementDocumentationStore((s) => s.fetchEvidenceItemsByProjectId);
  const updateRequirement = useRequirementsStore((s) => s.updateRequirement);
  const fetchVerificationCriteria = useRequirementsStore((s) => s.fetchVerificationCriteria);
  const categories = useRequirementDocumentationStore(
    useShallow((s) => s.categoriesByRequirement[requirement.id] ?? [])
  );
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (projectId) {
      void fetchEvidenceItemsByProjectId(projectId);
    }
    void fetchCategories(requirement.id).finally(() => setLoading(false));
  }, [requirement.id, fetchCategories, fetchEvidenceItemsByProjectId, projectId]);

  // The criteria table moved to the Verification tab; review progress still counts accepted criteria.
  useEffect(() => {
    void fetchVerificationCriteria(requirement.id);
  }, [requirement.id, fetchVerificationCriteria]);

  const orderedCategories = [
    ...categories.filter((c) => c.type === RequirementCategoryType.EVIDENCE),
    ...categories.filter((c) => c.type === RequirementCategoryType.DESIGN),
  ];

  return (
    <div className="d-flex flex-column gap-3">
      <div className="mt-2">
        <UserAssignmentSelect
          label={I18n.get('assignedReviewer')}
          assignedUserId={requirement.assignedUserId}
          onAssign={(assignedUserId) => void updateRequirement(requirement.id, { assignedUserId })}
        />
      </div>
      <ReviewProgressPanel requirement={requirement} />
      {loading ? (
        <ReviewCategoriesPlaceholder />
      ) : orderedCategories.length === 0 ? (
        <div className="list-card mt-4">
          <p className="text-muted small mb-0">{I18n.get('noReviewCategories')}</p>
        </div>
      ) : (
        orderedCategories.map((category) => (
          <ReviewCategorySection key={category.id} category={category} requirement={requirement} />
        ))
      )}
    </div>
  );
}

function ReviewCategoriesPlaceholder() {
  return (
    <div className="list-card mt-4">
      <Placeholder animation="glow">
        <Placeholder xs={4} size="lg" />
      </Placeholder>
      <Placeholder animation="glow" className="mt-2">
        <Placeholder xs={8} />
      </Placeholder>
      <Placeholder animation="glow" className="mt-2">
        <Placeholder xs={6} />
      </Placeholder>
    </div>
  );
}

function ReviewProgressPanel({ requirement }: { requirement: RequirementParams }) {
  const categories = useRequirementDocumentationStore(
    useShallow((s) => s.categoriesByRequirement[requirement.id] ?? [])
  );
  const submissionsByCategory = useRequirementDocumentationStore((s) => s.submissionsByCategory);
  const verificationCriteria = useRequirementsStore((s) => s.verificationCriteria)
    .filter((vc) => vc.requirementId === requirement.id);

  const progress = useMemo(
    () => computeReviewProgress(categories, submissionsByCategory),
    [categories, submissionsByCategory]
  );
  const acceptedCriteria = verificationCriteria.filter((vc) => vc.status === VerificationCriteriaStatus.ACCEPTED).length;

  return (
    <div className="list-card mt-4">
      <p className="list-card-title">{I18n.get('reviewProgress')}</p>
      <div className="d-flex align-items-center flex-wrap gap-3">
        <span>{I18n.get('submissionsReviewed', { reviewed: String(progress.reviewedCount), total: String(progress.totalReviewable) })}</span>
        {progress.awaitingSubmissionCount > 0 && (
          <div className="fusa-badge bg-primary">
            {I18n.get('awaitingSubmissionCount', { count: String(progress.awaitingSubmissionCount) })}
          </div>
        )}
        <span>{I18n.get('criteriaAccepted', { accepted: String(acceptedCriteria), total: String(verificationCriteria.length) })}</span>
      </div>
    </div>
  );
}

function ReviewCategorySection({ category, requirement }: { category: RequirementEvidenceCategory; requirement: RequirementParams }) {
  const submissions = useRequirementDocumentationStore(useShallow((s) => s.submissionsByCategory[category.id] ?? []));
  const reviewable = reviewableSubmissions(category, submissions);

  return (
    <div className="list-card mt-4">
      <p className="list-card-title">{category.name}</p>
      <p className="category-block-description">{I18n.get(category.type)}</p>
      {category.description && (
        <p className="category-block-description">{category.description}</p>
      )}
      {reviewable.length === 0 ? (
        <div className="list-card-empty">
          <p className="list-card-empty-text">
            {category.type === RequirementCategoryType.EVIDENCE ? I18n.get('awaitingSubmission') : I18n.get('noDesignsUploaded')}
          </p>
        </div>
      ) : (
        <div className="d-flex flex-wrap gap-3 mt-3">
          {reviewable.map((sub) => (
            <ReviewSubmissionCard key={sub.id} submission={sub} requirement={requirement} categoryType={category.type} />
          ))}
        </div>
      )}
    </div>
  );
}

function ReviewSubmissionCard({ submission, requirement, categoryType }: {
  submission: EvidenceSubmission;
  requirement: RequirementParams;
  categoryType: RequirementCategoryType;
}) {
  const evidenceItem = useRequirementDocumentationStore((s) => s.evidenceItems.find((i) => i.id === submission.evidenceItemId));

  if (!evidenceItem) {
    return null;
  }

  return (
    <div className="evidence-card">
      <FileCard file={evidenceItem.file} title={submission.title} readOnly={true} />
      <div className="evidence-card-body">
        {categoryType === RequirementCategoryType.EVIDENCE && <SubmissionInformation submission={submission} />}
        <EvidenceReviewSection submission={submission} requirement={requirement} allowReview={true} />
      </div>
    </div>
  );
}
